// State persistence and synchronization utilities
export class StateManager {
  private static instance: StateManager;
  private listeners: Map<string, Set<(value: any) => void>> = new Map();

  private constructor() {
    // Listen for storage changes across tabs
    window.addEventListener('storage', this.handleStorageChange.bind(this));
    window.addEventListener('localStorageChange', this.handleCustomStorageChange.bind(this));
  }

  static getInstance(): StateManager {
    if (!StateManager.instance) {
      StateManager.instance = new StateManager();
    }
    return StateManager.instance;
  }

  // Subscribe to state changes
  subscribe(key: string, callback: (value: any) => void): () => void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(key);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(key);
        }
      }
    };
  }

  // Set state with persistence and cross-tab sync
  setState<T>(key: string, value: T): void {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
      
      // Notify local listeners
      this.notifyListeners(key, value);
      
      // Notify other tabs
      window.dispatchEvent(new CustomEvent('localStorageChange', {
        detail: { key, value }
      }));
    } catch (error) {
      console.error(`Error setting state for key "${key}":`, error);
    }
  }

  // Get state from localStorage
  getState<T>(key: string, defaultValue: T): T {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Error getting state for key "${key}":`, error);
      return defaultValue;
    }
  }

  // Clear specific state
  clearState(key: string): void {
    localStorage.removeItem(key);
    this.notifyListeners(key, null);
    
    window.dispatchEvent(new CustomEvent('localStorageChange', {
      detail: { key, value: null }
    }));
  }

  // Clear all app state
  clearAllState(): void {
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith('pharmaplus-') || key.startsWith('supabase.')
    );
    
    keys.forEach(key => {
      localStorage.removeItem(key);
      this.notifyListeners(key, null);
    });
  }

  // Handle storage changes from other tabs
  private handleStorageChange(e: StorageEvent): void {
    if (e.key && e.newValue !== null) {
      try {
        const value = JSON.parse(e.newValue);
        this.notifyListeners(e.key, value);
      } catch (error) {
        console.error(`Error parsing storage value for key "${e.key}":`, error);
      }
    }
  }

  // Handle custom storage changes
  private handleCustomStorageChange(e: CustomEvent): void {
    const { key, value } = e.detail;
    this.notifyListeners(key, value);
  }

  // Notify all listeners for a specific key
  private notifyListeners(key: string, value: any): void {
    const callbacks = this.listeners.get(key);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(value);
        } catch (error) {
          console.error(`Error in state listener for key "${key}":`, error);
        }
      });
    }
  }

  // Backup state to a downloadable file
  exportState(): void {
    const state: Record<string, any> = {};
    
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('pharmaplus-')) {
        try {
          state[key] = JSON.parse(localStorage.getItem(key) || '');
        } catch (error) {
          state[key] = localStorage.getItem(key);
        }
      }
    });

    const blob = new Blob([JSON.stringify(state, null, 2)], { 
      type: 'application/json' 
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pharmaplus-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Import state from a backup file
  importState(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const state = JSON.parse(e.target?.result as string);
          
          Object.entries(state).forEach(([key, value]) => {
            if (key.startsWith('pharmaplus-')) {
              this.setState(key, value);
            }
          });
          
          resolve();
        } catch (error) {
          reject(new Error('Invalid backup file format'));
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read backup file'));
      reader.readAsText(file);
    });
  }

  // Get app state summary for debugging
  getStateSummary(): Record<string, any> {
    const summary: Record<string, any> = {};
    
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('pharmaplus-')) {
        try {
          const value = JSON.parse(localStorage.getItem(key) || '');
          summary[key] = {
            type: typeof value,
            size: JSON.stringify(value).length,
            lastModified: new Date().toISOString()
          };
        } catch (error) {
          summary[key] = {
            type: 'string',
            size: (localStorage.getItem(key) || '').length,
            error: 'Parse error'
          };
        }
      }
    });
    
    return summary;
  }
}

// Export singleton instance
export const stateManager = StateManager.getInstance();

// Utility functions for common state operations
export const persistentState = {
  // Theme management
  theme: {
    get: () => stateManager.getState('pharmaplus-theme', { theme: 'light', accentColor: 'green' }),
    set: (theme: any) => stateManager.setState('pharmaplus-theme', theme),
    subscribe: (callback: (theme: any) => void) => stateManager.subscribe('pharmaplus-theme', callback)
  },

  // User preferences
  preferences: {
    get: () => stateManager.getState('pharmaplus-preferences', {}),
    set: (prefs: any) => stateManager.setState('pharmaplus-preferences', prefs),
    subscribe: (callback: (prefs: any) => void) => stateManager.subscribe('pharmaplus-preferences', callback)
  },

  // Cart state
  cart: {
    get: () => stateManager.getState('pharmaplus-cart', []),
    set: (cart: any[]) => stateManager.setState('pharmaplus-cart', cart),
    subscribe: (callback: (cart: any[]) => void) => stateManager.subscribe('pharmaplus-cart', callback)
  },

  // Navigation state
  navigation: {
    get: () => stateManager.getState('pharmaplus-navigation', { activeSection: 'discover' }),
    set: (nav: any) => stateManager.setState('pharmaplus-navigation', nav),
    subscribe: (callback: (nav: any) => void) => stateManager.subscribe('pharmaplus-navigation', callback)
  }
};